import { Block, Blocks } from '@chameleoncreator/creator-app';

export enum TrackerState {
  DIRTY,
  CLEAN,
  DELETED,
}

export interface Tracker {
  [key: string]: TrackerState;
}

interface Timing {
  firstLoad: number;
  complete: number;
  buttonClick: { [key: string]: number };
}

export interface Report {
  key?: number;
  id: string;
  question?: string | undefined;
  reportable: 'multichoice' | 'sortation';
  type: string;
  response: string;
  correct?: string;
  result: 0 | 1;
}

export interface AssessmentData {
  scoring: boolean;
  score: number;
}

export interface QuestionData {
  id: string;
  questionName: string;
  answer?: string[];
  correctAnswer: string[];
  isCorrect?: boolean | undefined;
  attemptCount?: string | number;
  navigateToResults?: boolean;
  isLocked?: boolean;
  submitButtonLabel?: string;
  hasNotBeenAttempted?: boolean | undefined;
}

export interface Question {
  [key: string]: QuestionData;
}

export interface Assessment {
  [key: string]: {
    data?: AssessmentData;
    results?: {
      [key: string]: Question;
    };
  };
}

export interface Assessments {
  [key: string]: Assessment;
}

export type Audio = {
  id?: string;
  src: string;
  audioSrc: string;
  autoPlay?: boolean;
  controls?: boolean;
  delay?: number;
  pause?: boolean;
} | null;

export interface NextState {
  apiError: { count: number; err: any };
  blocks: { [key: string]: Block };
  bookmarkLoaded: boolean;
  bookmarkLocation: string | undefined;
  currentInteraction?: string;
  currentInteractionBlockId?: string | null;
  currentTopic?: string;
  audio?: Audio;

  audioAcknowledged: boolean;
  customFonts: any[];
  error: any;
  exit: boolean;
  exitComplete: boolean;
  feedback: {
    [key: string]: {
      key: string;
      rating: number;
      message: string;
      identity: string;
      reference: string;
    };
  };
  focusedNodeId?: string;
  hasError: boolean;
  hideHeader: boolean;
  header?: Block;
  standardHeader?: Block;
  graphicHeader?: Block;
  standardHeaderLogo?: Block;
  graphicHeaderBackground?: Block;
  graphicHeaderLogo?: Block;
  interactionsLocked: string[];
  interactionsOpen: string[];
  interactionsSeen: string[];
  isScrolling: boolean;
  lastFocusedNodeId?: string;
  links: any;
  mirrorActive: boolean;
  idToSIdMap: { [key: string]: number };
  sIdToIdMap: { [key: string]: number };
  nextInteractionMap: { [key: string]: string };
  projectId?: string;
  rootBlockId: string | undefined;
  scormSettings: {
    passMark: number;
    setCompleteFromScore: boolean;
    setCompleteOnExit: boolean;
    setCompleteWhenAllPagesViewed: boolean;
  };
  theme: {
    backgroundColor: string;
    buttonBackgroundColor: string;
    buttonBorderRadius: number;
    buttonOutline: boolean;
    buttonShadow: boolean;
    buttonShape: string;
    useLargeButton: boolean;
    buttonTextColor: string;
    buttonHover: string;
    fontFamilies: any[];
    fontFamily: string;
    fontSizes: {
      'Large screen': number;
      'Medium screen': number;
      'Small screen': number;
    };
    highContrast: boolean;
    recoloredChameleonImages: any;
    textColor: string;
    useImagePreloader: boolean;
    enableBookmarking: boolean;
    disableLockDownInPreview: boolean;
    userPalette: any[];
    menu: {
      'Menu background colour': string;
      'Menu text and close colour': string;
      'Menu text hover colour': string;
      'Menu text active colour': string;
    };
    headings: {
      h1: string | number;
      h2: string | number;
      h3: string | number;
      h4: string | number;
      h5: string | number;
      h6: string | number;
    };
    links: {
      link: string;
      visited: string;
      hover: string;
      focus: string;
      active: string;
    };
    linksSecondary: {
      link: string;
      visited: string;
      hover: string;
      focus: string;
      active: string;
    };
  };
  themeId?: string;
  tracker: Tracker;
  userBehaviour: {
    interactionTiming: { [key: string]: Timing };
    score: { [key: string]: 0 | 1 };
    scormResponses: {
      [key: string]: Report;
    };
  };
  assessments: Assessments;
}

export type State = NextState;

export type Action = { type: string; data: { [key: string]: any } };

export const initialState: NextState = {
  apiError: { count: 0, err: undefined },
  blocks: {},
  bookmarkLoaded: false,
  bookmarkLocation: undefined,
  currentInteraction: undefined,
  currentInteractionBlockId: undefined,
  currentTopic: undefined,
  audio: null,
  audioAcknowledged: false,
  customFonts: [],
  error: undefined,
  exit: false,
  exitComplete: false,
  focusedNodeId: undefined,
  feedback: {},
  hasError: false,
  hideHeader: false,
  standardHeader: undefined,
  graphicHeader: undefined,
  standardHeaderLogo: undefined,
  graphicHeaderBackground: undefined,
  graphicHeaderLogo: undefined,
  interactionsLocked: [],
  interactionsOpen: [],
  interactionsSeen: [],
  isScrolling: false,
  lastFocusedNodeId: undefined,
  links: {},
  mirrorActive: false,
  idToSIdMap: {},
  sIdToIdMap: {},
  nextInteractionMap: {},
  projectId: undefined,
  rootBlockId: undefined,
  scormSettings: {
    passMark: 80,
    setCompleteFromScore: false,
    setCompleteOnExit: false,
    setCompleteWhenAllPagesViewed: true,
  },
  theme: {
    backgroundColor: '#ffffff',
    buttonBackgroundColor: '#060709',
    buttonBorderRadius: 0,
    buttonOutline: false,
    buttonShadow: false,
    buttonShape: 'pill',
    useLargeButton: false,
    buttonTextColor: '#ffffff',
    buttonHover: '',
    fontFamilies: [],
    fontFamily: '',
    fontSizes: {
      'Large screen': 16,
      'Medium screen': 16,
      'Small screen': 16,
    },

    highContrast: false,
    recoloredChameleonImages: {},
    textColor: '#060709',
    useImagePreloader: true,
    enableBookmarking: true,
    disableLockDownInPreview: false,
    userPalette: [],
    menu: {
      'Menu background colour': '#ffffff',
      'Menu text and close colour': '#000000',
      'Menu text hover colour': '#0000ff',
      'Menu text active colour': '#ff0000',
    },
    headings: {
      h1: 1,
      h2: 1,
      h3: 1,
      h4: 1,
      h5: 1,
      h6: 1,
    },
    links: {
      link: '#0000ff',
      visited: '#800080',
      hover: '#0000ff',
      focus: '#e59700',
      active: '#ff0000',
    },
    linksSecondary: {
      link: '#0000ff',
      visited: '#800080',
      hover: '#0000ff',
      focus: '#e59700',
      active: '#ff0000',
    },
  },
  themeId: undefined,
  tracker: {},
  userBehaviour: {
    score: {},
    interactionTiming: {},
    scormResponses: {},
  },
  assessments: {},
};
